import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authQueryKeys } from '@/shared/hooks/queries/queryKeys';
import { broadcastLogout } from '@/shared/hooks/useCrossTabLogout';
import { useRecentConversationsStore } from '@/shared/stores/recentConversations.store';

/**
 * Returns a cleanup function that clears auth state and redirects to login.
 * Used by both logout and account deletion to keep the two paths in sync.
 * Cancels in-flight queries before clearing the cache so background fetches
 * that complete after the session ends don't trigger the 401 refresh interceptor.
 * Also clears the persisted Recent Conversations store so a deleted or logged-out
 * user's chat list (and message previews) cannot resurface for the next login on
 * the same device — the store is a single localStorage key, not scoped per user.
 */
export const useAuthCleanup = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return () => {
    queryClient.cancelQueries().catch(() => {});
    queryClient.setQueryData(authQueryKeys.currentUser, null);
    queryClient.clear();
    useRecentConversationsStore.getState().clearSessions();
    broadcastLogout();
    navigate('/login', { replace: true });
  };
};
